import "./App.css";
import { Routes, Route, Navigate } from "react-router";
import ThemeCustomization from "./themes";
import ScrollTop from "./container/ScrollTop";
import CssBaseline from "@mui/material/CssBaseline";

// Public / Auth components
import NotFound from "./container/NotFound";

// Layouts and route guards (part of the first load)
import {
  ContactUsRoute,
  DistrictRoute,
  InternalRoute,
  OnboardRoute,
  PageSuspense,
  ProtectedRoute,
  PublicRoute,
  VerifyCompleteRoutes,
  VerifyRoutes,
} from "./routes";
import { AppProviders } from "./contexts";
import { AuthProvider } from "./contexts/AuthContext";
import { useEffect } from "react";
import { localUrlEnabled } from "service/axios/axiosInstance";
import InternalLayout from "routes/layout/InternalLayout";
import MainLayout from "routes/layout/MainLayout";
import AccessMessagePage from "./components/layout/AccessMessagePage";

// Pages: loaded on demand (routes/pages.js)
import {
  ActiveDirectoryContainer,
  ActiveDirectoryLayout,
  AddBuilding,
  AddCampus,
  AddDomainContainer,
  AddTagContainer,
  AssignTagActiveDirectoryContainer,
  BuildingsContainer,
  CampusContainer,
  Directory,
  DistrictDashboard,
  DomainContainer,
  DomainLEContainer,
  DomainLayout,
  DomainUserContainer,
  EditActiveDirectory,
  EditBuilding,
  EditCampus,
  EditDistrict,
  IncidentContainer,
  InternalAdminContainer,
  InternalPoliciesContainer,
  InternalTagsContainer,
  InviteAdminContainer,
  LocalURL,
  LockdownStageContainer,
  LoginContainer,
  LoginVerifyContainer,
  OnboardAdminContainer,
  OnboardCampusContainer,
  OnboardDistrictContainer,
  OnboardingDirectory,
  ProfileSettingContainer,
  Settings,
  SignupCompleteContainer,
  SignupContainer,
  SignupVerifyCompleteContainer,
  SignupVerifyContainer,
  SuccessContainer,
  ViewCampusLayout,
  WelcomeScreen,
} from "./routes/pages";

const App = () => {
  useEffect(() => {
    document.documentElement.dataset.appVersion =
      import.meta.env.REACT_APP_VERSION ?? "";
  }, []);
  return (
    <ThemeCustomization>
      <ScrollTop>
        {/* Reset CSS and apply Material UI theme baseline */}
        <CssBaseline enableColorScheme />

        {/* Main Layout wrapper */}
        <AppProviders>
          {/* Fallback for lazy pages outside the layouts' own boundaries */}
          <PageSuspense>
            <Routes>
              {/* API URL override page: dev server / REACT_APP_ENABLE_LOCAL_URL only */}
              {localUrlEnabled && <Route path="/localUrl" element={<LocalURL />} />}
              {/* ---------------- PUBLIC ROUTES ---------------- */}

              <Route element={<AuthProvider />}>
                {/* <Route path="accessMessagePage" element={<AccessMessagePage />} /> */}
                <Route element={<PublicRoute />}>
                  <Route path="welcome_screen" element={<WelcomeScreen />} />
                  <Route path="login">
                    <Route index element={<LoginContainer />} />
                    <Route path="verify" element={<VerifyRoutes />}>
                      <Route index element={<LoginVerifyContainer />} />
                    </Route>
                  </Route>
                  <Route path="signup">
                    <Route index element={<SignupContainer />} />
                    <Route path="verify" element={<VerifyRoutes />}>
                      <Route index element={<SignupVerifyContainer />} />
                    </Route>
                  </Route>
                </Route>

                {/* ---------------- AUTH ROUTES ---------------- */}
                <Route path="" element={<ProtectedRoute />}>
                  <Route
                    path="verify_complete"
                    element={<VerifyCompleteRoutes />}
                  >
                    <Route index element={<SignupVerifyCompleteContainer />} />
                  </Route>
                  <Route path="contactAdmin" element={<ContactUsRoute />}>
                    <Route index element={<AccessMessagePage />} />
                  </Route>

                  <Route element={<DistrictRoute />}>
                    <Route element={<InternalRoute />}>
                      <Route element={<MainLayout />}>
                        <Route
                          exact
                          path="district/:districtId"
                          element={<EditDistrict />}
                        />
                        <Route
                          exact
                          path="profile_setting"
                          element={<ProfileSettingContainer />}
                        />
                      </Route>
                      <Route element={<InternalLayout />}>
                        {/* INTERNAL MODULE (district-specific subroutes) */}
                        <Route index element={<Navigate to="home" replace />} />
                        {/* Dashboard inside a district */}
                        <Route exact path="home" element={<DistrictDashboard />}>
                          {/* <Route index element={<DistrictDashboard />} /> */}
                        </Route>

                        {/* <Route
                        path="ongoing_incidents"
                        element={<OngoingIncidentsContainer />}
                      /> */}

                        {/* Active Directory (staff, students, lawEnforcement) */}
                        <Route path="users" element={<ActiveDirectoryLayout />}>
                          <Route
                            index
                            element={<Navigate to="staff" replace />}
                          />

                          {/* Staff subroutes */}
                          <Route path="staff">
                            <Route
                              index
                              element={
                                <ActiveDirectoryContainer status={"staff"} />
                              }
                            />
                            <Route path=":adId">
                              <Route
                                index
                                element={<EditActiveDirectory status={"staff"} />}
                              />
                            </Route>
                          </Route>

                          {/* Students subroutes */}
                          {/* <Route path="students">
                          <Route
                            index
                            element={
                              <ActiveDirectoryContainer
                                status={"students"}
                                key={"students"}
                              />
                            }
                          />
                          <Route path=":adId">
                            <Route
                              index
                              element={
                                <EditActiveDirectory status={"students"} />
                              }
                            />
                          </Route>
                        </Route> */}

                          {/* Law enforcement subroutes */}
                          <Route path="lawEnforcement">
                            <Route
                              index
                              element={
                                <ActiveDirectoryContainer
                                  status={"lawEnforcement"}
                                  key={"lawEnforcement"}
                                />
                              }
                            />
                            <Route path=":adId">
                              <Route
                                index
                                element={
                                  <EditActiveDirectory
                                    status={"lawEnforcement"}
                                  />
                                }
                              />
                            </Route>
                          </Route>
                        </Route>

                        {/* School & campus management */}
                        <Route path="school">
                          <Route
                            index
                            element={<Navigate to="campus" replace />}
                          />
                          <Route path="campus">
                            <Route index element={<CampusContainer />} />
                            <Route path="add_campus" element={<AddCampus />} />
                            <Route
                              path=":campusId"
                              element={<ViewCampusLayout />}
                            >
                              <Route
                                index
                                element={<Navigate to="detail" replace />}
                              />

                              {/* Campus details tab */}
                              <Route
                                path="detail"
                                element={<EditCampus tabStatus="detail" />}
                              />
                              {/* Campus buildings tab */}
                              <Route path="buildings">
                                <Route
                                  index
                                  element={
                                    <BuildingsContainer tabStatus="buildings" />
                                  }
                                />
                                <Route
                                  path=":buildingId"
                                  element={<EditBuilding tabStatus="buildings" />}
                                />
                                <Route
                                  path="add_building"
                                  element={<AddBuilding tabStatus="buildings" />}
                                />
                              </Route>

                              <Route element={<ActiveDirectoryLayout />}>
                                {/* Campus staff tab */}
                                <Route path="staff">
                                  <Route
                                    index
                                    element={
                                      <ActiveDirectoryContainer
                                        status={"staff"}
                                      />
                                    }
                                  />
                                  <Route path=":adId">
                                    <Route
                                      index
                                      element={
                                        <EditActiveDirectory status={"staff"} />
                                      }
                                    />
                                  </Route>
                                </Route>

                                {/* Campus students tab */}
                                {/* <Route path="students">
                                <Route
                                  index
                                  element={
                                    <ActiveDirectoryContainer
                                      status={"students"}
                                      key={"students"}
                                    />
                                  }
                                />
                                <Route path=":adId">
                                  <Route
                                    index
                                    element={
                                      <EditActiveDirectory status={"students"} />
                                    }
                                  />
                                </Route>
                              </Route> */}

                                {/* Campus lawEnforcement tab */}
                                <Route path="lawEnforcement">
                                  <Route
                                    index
                                    element={
                                      <ActiveDirectoryContainer
                                        status={"lawEnforcement"}
                                        key={"lawEnforcement"}
                                      />
                                    }
                                  />
                                  <Route path=":adId">
                                    <Route
                                      index
                                      element={
                                        <EditActiveDirectory
                                          status={"lawEnforcement"}
                                        />
                                      }
                                    />
                                  </Route>
                                </Route>
                              </Route>
                            </Route>
                          </Route>

                          {/* Direct buildings route */}
                          <Route path="buildings">
                            <Route index element={<BuildingsContainer />} />
                            <Route
                              path="add_building"
                              element={<AddBuilding />}
                            />
                            <Route path=":buildingId">
                              <Route index element={<EditBuilding />} />
                            </Route>
                          </Route>
                        </Route>
                        <Route path="domains">
                          <Route index element={<Navigate to="list" replace />} />
                          <Route path="list">
                            <Route path="" element={<DomainContainer />}>
                              <Route
                                path="add"
                                element={<AddDomainContainer />}
                              />
                            </Route>
                            <Route path=":domainId" element={<DomainLayout />}>
                              <Route index element={<DomainUserContainer />} />
                            </Route>
                          </Route>
                          <Route exact path="911Dispatcher">
                            <Route index element={<DomainLEContainer />} />
                          </Route>
                        </Route>

                        <Route
                          path="incidents"
                          element={<IncidentContainer status={"staff"} />}
                        />

                        {/* District settings */}
                        <Route path="settings" element={<Settings />}>
                          <Route
                            index
                            element={<Navigate to="admins" replace />}
                          />
                          <Route
                            path="admins"
                            element={<InternalAdminContainer />}
                          >
                            <Route
                              path="add_invite"
                              element={<InviteAdminContainer />}
                            />
                            <Route
                              path="show_success"
                              element={<SuccessContainer />}
                            />
                          </Route>
                          <Route
                            path="policies"
                            element={<InternalPoliciesContainer />}
                          />
                          <Route
                            path="lockdown_stage"
                            element={<LockdownStageContainer />}
                          />
                          <Route path="directory" element={<Directory />} />

                          {/* Tags management */}
                          <Route path="tags">
                            {/* Keeps InternalTags open, adds CreateTag inside via <Outlet /> */}
                            <Route path="" element={<InternalTagsContainer />}>
                              <Route
                                path="add_tag"
                                element={<AddTagContainer />}
                              />
                            </Route>

                            {/* Replaces InternalTags completely */}
                            <Route
                              path="assignUser/:tagId"
                              element={
                                <AssignTagActiveDirectoryContainer
                                  status={"allUsers"}
                                />
                              }
                            />
                          </Route>
                        </Route>

                        {/* Catch-all fallback inside Internal */}
                        <Route path="*" element={<NotFound path={"home"} />} />
                      </Route>
                    </Route>
                    <Route path="onboarding" element={<OnboardRoute />}>
                      <Route
                        path="create_district"
                        element={<OnboardDistrictContainer />}
                      />
                      <Route
                        path="invite_admin"
                        element={<OnboardAdminContainer />}
                      />
                      <Route path="saml_auth" element={<OnboardingDirectory />} />
                      <Route
                        path="create_campus"
                        element={<OnboardCampusContainer />}
                      />
                      {/* <Route
                        path="create_building"
                        element={<OnboardBuildingContainer />}
                      /> */}
                      <Route
                        path="complete"
                        element={<SignupCompleteContainer />}
                      />
                    </Route>
                  </Route>
                </Route>

                {/* Global fallback */}
                <Route path="*" element={<NotFound path={""} />} />
              </Route>
            </Routes>
          </PageSuspense>
        </AppProviders>
      </ScrollTop>
    </ThemeCustomization>
  );
};

export default App;
